import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth, isAdmin } from "../util/SecurityUtils";
import { useBoardNotes } from "../hooks/queries/useBoardNotes";
import { useCreateNote, useDeleteNote, useMoveNote, useVoteNote } from "../hooks/mutations/useBoardMutations";
import StickyNoteView from "../components/board/StickyNoteView";
import NoteEditorModal from "../components/board/NoteEditorModal";
import ConfirmModal from "../components/common/ConfirmModal";
import type { StickyNote } from "../types/domain";
import { BOARD_HEIGHT, BOARD_WIDTH, NOTE_HEADER_HEIGHT, NOTE_HEIGHT, NOTE_WIDTH } from "../util/boardConstants";

const MAX_SCALE = 2.5;
/** Pointer travel below this (in px) counts as a tap/click, above it as a drag. */
const TAP_THRESHOLD_PX = 8;

interface View {
  x: number;
  y: number;
  scale: number;
}

interface PendingNote {
  imageData: string;
  paperColor: string;
  pinColor: string;
  poll: boolean;
}

/** Vertical offset of the pin center inside the note wrapper — matches the CSS transform-origin. */
const PIN_OFFSET_Y = 18;

function Board() {
  const { t, i18n } = useTranslation();
  const { user, validated } = useAuth();
  const admin = isAdmin();
  const canUseBoard = !!user?.playerId;

  const { data: notes, isLoading } = useBoardNotes(canUseBoard);
  const createNote = useCreateNote();
  const moveNote = useMoveNote();
  const voteNote = useVoteNote();
  const deleteOwnNote = useDeleteNote(false);
  const deleteAdminNote = useDeleteNote(true);

  const [showEditor, setShowEditor] = useState(false);
  const [pendingNote, setPendingNote] = useState<PendingNote | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StickyNote | null>(null);
  const [detailNote, setDetailNote] = useState<StickyNote | null>(null);
  const [rotatingNote, setRotatingNote] = useState<StickyNote | null>(null);

  const viewportRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<View>({ x: 0, y: 0, scale: 0.4 });
  const viewInitialized = useRef(false);
  // The view lives in refs and is applied straight to the DOM — re-rendering all
  // notes on every pointermove would be too janky, especially on mobile.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({
    mode: null as "pan" | "pinch" | null,
    startView: { x: 0, y: 0, scale: 0.4 } as View,
    startX: 0,
    startY: 0,
    startDist: 1,
    startMidX: 0,
    startMidY: 0,
    moved: 0,
  });
  /** Pointer travel of the last finished gesture — gates note onClick after a pan. */
  const lastMoved = useRef(0);
  const noteDrag = useRef<{
    id: number;
    el: HTMLDivElement;
    startClientX: number;
    startClientY: number;
    origX: number;
    origY: number;
    moved: number;
  } | null>(null);
  const ghostPos = useRef({ x: BOARD_WIDTH / 2, y: BOARD_HEIGHT / 2 });
  // Rotation mode: current angle + the rotated note's DOM element live in refs so the
  // note can follow the pointer without re-rendering the whole board.
  const rotationValue = useRef(0);
  const rotateEl = useRef<HTMLElement | null>(null);
  const rotateDrag = useRef<{ pointerId: number; startAngle: number; startRotation: number } | null>(null);

  const applyView = useCallback(() => {
    const v = viewRef.current;
    if (surfaceRef.current) {
      surfaceRef.current.style.transform = `translate(${v.x}px, ${v.y}px) scale(${v.scale})`;
    }
  }, []);

  const fitScale = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return 0.4;
    return Math.min(vp.clientWidth / BOARD_WIDTH, vp.clientHeight / BOARD_HEIGHT);
  }, []);

  const clampView = useCallback((v: View): View => {
    const vp = viewportRef.current;
    if (!vp) return v;
    const minScale = fitScale() * 0.9;
    const scale = Math.min(Math.max(v.scale, minScale), MAX_SCALE);
    const bw = BOARD_WIDTH * scale;
    const bh = BOARD_HEIGHT * scale;
    const vw = vp.clientWidth;
    const vh = vp.clientHeight;
    // When the board is smaller than the viewport it stays centered, otherwise its
    // edges can't be dragged past the viewport edges.
    const x = Math.min(Math.max(v.x, Math.min(vw - bw, (vw - bw) / 2)), Math.max(0, (vw - bw) / 2));
    const y = Math.min(Math.max(v.y, Math.min(vh - bh, (vh - bh) / 2)), Math.max(0, (vh - bh) / 2));
    return { x, y, scale };
  }, [fitScale]);

  const setView = useCallback((v: View) => {
    viewRef.current = clampView(v);
    applyView();
  }, [applyView, clampView]);

  const zoomAround = useCallback((viewportX: number, viewportY: number, factor: number) => {
    const v = viewRef.current;
    const scale = Math.min(Math.max(v.scale * factor, fitScale() * 0.9), MAX_SCALE);
    const ratio = scale / v.scale;
    setView({
      x: viewportX - (viewportX - v.x) * ratio,
      y: viewportY - (viewportY - v.y) * ratio,
      scale,
    });
  }, [fitScale, setView]);

  const zoomFromCenter = useCallback((factor: number) => {
    const vp = viewportRef.current;
    if (!vp) return;
    zoomAround(vp.clientWidth / 2, vp.clientHeight / 2, factor);
  }, [zoomAround]);

  const fitView = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const scale = fitScale();
    setView({
      x: (vp.clientWidth - BOARD_WIDTH * scale) / 2,
      y: (vp.clientHeight - BOARD_HEIGHT * scale) / 2,
      scale,
    });
  }, [fitScale, setView]);

  const toBoardCoords = useCallback((clientX: number, clientY: number) => {
    const vp = viewportRef.current!;
    const rect = vp.getBoundingClientRect();
    const v = viewRef.current;
    return {
      x: (clientX - rect.left - v.x) / v.scale,
      y: (clientY - rect.top - v.y) / v.scale,
    };
  }, []);

  // Initial view: slightly zoomed-in on the board center once the viewport has a size.
  useEffect(() => {
    if (!canUseBoard || viewInitialized.current) return;
    const vp = viewportRef.current;
    if (!vp) return;
    viewInitialized.current = true;
    const scale = Math.min(Math.max(fitScale() * 1.4, fitScale()), MAX_SCALE);
    setView({
      x: (vp.clientWidth - BOARD_WIDTH * scale) / 2,
      y: (vp.clientHeight - BOARD_HEIGHT * scale) / 2,
      scale,
    });
  }, [canUseBoard, fitScale, setView]);

  // Wheel zoom needs a native non-passive listener to preventDefault page scroll.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      zoomAround(e.clientX - rect.left, e.clientY - rect.top, Math.exp(-e.deltaY * 0.0015));
    };
    vp.addEventListener("wheel", onWheel, { passive: false });
    return () => vp.removeEventListener("wheel", onWheel);
  }, [canUseBoard, zoomAround]);

  useEffect(() => {
    const onResize = () => setView(viewRef.current);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [setView]);

  const updateGhost = useCallback((boardX: number, boardY: number) => {
    ghostPos.current = { x: boardX, y: boardY };
    if (ghostRef.current) {
      ghostRef.current.style.left = `${boardX - NOTE_WIDTH / 2}px`;
      ghostRef.current.style.top = `${boardY - NOTE_HEIGHT / 2}px`;
    }
  }, []);

  const placeNoteAt = (clientX: number, clientY: number) => {
    if (!pendingNote || createNote.isPending) return;
    const { x, y } = toBoardCoords(clientX, clientY);
    const clampedX = Math.min(Math.max(x, NOTE_WIDTH / 2), BOARD_WIDTH - NOTE_WIDTH / 2);
    const clampedY = Math.min(Math.max(y, NOTE_HEIGHT / 2), BOARD_HEIGHT - NOTE_HEIGHT / 2);
    createNote.mutate(
      { ...pendingNote, x: clampedX, y: clampedY, rotation: 0 },
      {
        onSuccess: () => setPendingNote(null),
        onError: () => alert(t("boardCreateFailed")),
      },
    );
  };

  // --- rotation mode ----------------------------------------------------------

  /** Angle (deg) of the pointer around the note's pin, which is the rotation origin. */
  const pointerAngle = (note: StickyNote, clientX: number, clientY: number) => {
    const rect = viewportRef.current!.getBoundingClientRect();
    const v = viewRef.current;
    const pinX = rect.left + v.x + note.x * v.scale;
    const pinY = rect.top + v.y + (note.y - NOTE_HEIGHT / 2 + PIN_OFFSET_Y) * v.scale;
    return (Math.atan2(clientY - pinY, clientX - pinX) * 180) / Math.PI;
  };

  const startRotating = (note: StickyNote) => {
    if (rotatingNote || pendingNote) return;
    rotationValue.current = note.rotation;
    rotateEl.current = surfaceRef.current?.querySelector(`[data-note-id="${note.id}"]`) as HTMLElement | null;
    setRotatingNote(note);
  };

  const stopRotating = (finalRotation: number) => {
    if (rotateEl.current) rotateEl.current.style.transform = `rotate(${finalRotation}deg)`;
    rotateEl.current = null;
    rotateDrag.current = null;
    setRotatingNote(null);
  };

  const confirmRotation = () => {
    if (!rotatingNote) return;
    const rotation = ((rotationValue.current % 360) + 360) % 360;
    moveNote.mutate({ id: rotatingNote.id, x: rotatingNote.x, y: rotatingNote.y, rotation });
    stopRotating(rotation);
  };

  const cancelRotation = () => {
    if (!rotatingNote) return;
    stopRotating(rotatingNote.rotation);
  };

  // --- viewport pan / pinch / tap-to-place -----------------------------------

  const onViewportPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const vp = viewportRef.current;
    if (!vp) return;
    if (rotatingNote) {
      // Rotation mode repurposes the whole viewport: dragging anywhere spins the note.
      if (rotateDrag.current) return; // ignore extra fingers
      vp.setPointerCapture(e.pointerId);
      rotateDrag.current = {
        pointerId: e.pointerId,
        startAngle: pointerAngle(rotatingNote, e.clientX, e.clientY),
        startRotation: rotationValue.current,
      };
      return;
    }
    vp.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (pointers.current.size === 1) {
      g.mode = "pan";
      g.startView = { ...viewRef.current };
      g.startX = e.clientX;
      g.startY = e.clientY;
      g.moved = 0;
    } else if (pointers.current.size === 2) {
      const [p1, p2] = Array.from(pointers.current.values());
      g.mode = "pinch";
      g.startView = { ...viewRef.current };
      g.startDist = Math.max(Math.hypot(p2.x - p1.x, p2.y - p1.y), 1);
      g.startMidX = (p1.x + p2.x) / 2;
      g.startMidY = (p1.y + p2.y) / 2;
      g.moved = Number.MAX_SAFE_INTEGER;
    }
  };

  const onViewportPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const rd = rotateDrag.current;
    if (rotatingNote && rd && e.pointerId === rd.pointerId) {
      const angle = pointerAngle(rotatingNote, e.clientX, e.clientY);
      rotationValue.current = rd.startRotation + (angle - rd.startAngle);
      if (rotateEl.current) rotateEl.current.style.transform = `rotate(${rotationValue.current}deg)`;
      return;
    }
    // Mouse hover (no buttons): let the placement ghost follow the cursor.
    if (pendingNote && pointers.current.size === 0) {
      const { x, y } = toBoardCoords(e.clientX, e.clientY);
      updateGhost(x, y);
    }
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    const rect = viewportRef.current!.getBoundingClientRect();
    if (g.mode === "pan" && pointers.current.size === 1) {
      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;
      g.moved = Math.max(g.moved, Math.hypot(dx, dy));
      setView({ x: g.startView.x + dx, y: g.startView.y + dy, scale: g.startView.scale });
    } else if (g.mode === "pinch" && pointers.current.size >= 2) {
      const [p1, p2] = Array.from(pointers.current.values());
      const dist = Math.max(Math.hypot(p2.x - p1.x, p2.y - p1.y), 1);
      const midX = (p1.x + p2.x) / 2 - rect.left;
      const midY = (p1.y + p2.y) / 2 - rect.top;
      const startMidX = g.startMidX - rect.left;
      const startMidY = g.startMidY - rect.top;
      const scale = Math.min(Math.max(g.startView.scale * (dist / g.startDist), fitScale() * 0.9), MAX_SCALE);
      // Keep the board point that was under the initial midpoint under the current midpoint.
      const boardX = (startMidX - g.startView.x) / g.startView.scale;
      const boardY = (startMidY - g.startView.y) / g.startView.scale;
      setView({ x: midX - boardX * scale, y: midY - boardY * scale, scale });
    }
  };

  const onViewportPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (rotatingNote) {
      if (rotateDrag.current?.pointerId === e.pointerId) rotateDrag.current = null;
      return;
    }
    if (!pointers.current.has(e.pointerId)) return;
    const g = gesture.current;
    const wasTap = g.mode === "pan" && g.moved < TAP_THRESHOLD_PX;
    lastMoved.current = g.moved;
    pointers.current.delete(e.pointerId);
    if (wasTap && pendingNote) {
      placeNoteAt(e.clientX, e.clientY);
    } else if (wasTap) {
      // Pointer capture retargets the up/click events to the viewport, so a tap on a
      // foreign note never reaches the note element — resolve it by hit-testing instead.
      // (Own notes capture the pointer themselves and open the detail in their handler.)
      const hit = document.elementFromPoint(e.clientX, e.clientY)?.closest(".board-note");
      const idAttr = hit?.getAttribute("data-note-id");
      if (idAttr) {
        const note = notes?.find((n) => n.id === Number(idAttr));
        if (note && !note.own) setDetailNote(note);
      }
    }
    if (pointers.current.size === 1) {
      // Pinch ended with one finger still down — continue as pan from its position.
      const [remaining] = Array.from(pointers.current.values());
      g.mode = "pan";
      g.startView = { ...viewRef.current };
      g.startX = remaining.x;
      g.startY = remaining.y;
      g.moved = Number.MAX_SAFE_INTEGER; // no tap after a pinch
    } else if (pointers.current.size === 0) {
      g.mode = null;
    }
  };

  // --- own-note dragging ------------------------------------------------------

  const onNotePointerDown = (note: StickyNote) => (e: React.PointerEvent<HTMLDivElement>) => {
    // In rotation mode the event bubbles to the viewport, which handles the spin.
    if (!note.own || pendingNote || rotatingNote) return;
    e.stopPropagation();
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    noteDrag.current = {
      id: note.id,
      el,
      startClientX: e.clientX,
      startClientY: e.clientY,
      origX: note.x,
      origY: note.y,
      moved: 0,
    };
  };

  const onNotePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = noteDrag.current;
    if (!drag || e.currentTarget !== drag.el) return;
    const scale = viewRef.current.scale;
    const dx = (e.clientX - drag.startClientX) / scale;
    const dy = (e.clientY - drag.startClientY) / scale;
    drag.moved = Math.max(drag.moved, Math.hypot(e.clientX - drag.startClientX, e.clientY - drag.startClientY));
    const x = Math.min(Math.max(drag.origX + dx, NOTE_WIDTH / 2), BOARD_WIDTH - NOTE_WIDTH / 2);
    const y = Math.min(Math.max(drag.origY + dy, NOTE_HEIGHT / 2), BOARD_HEIGHT - NOTE_HEIGHT / 2);
    drag.el.style.left = `${x - NOTE_WIDTH / 2}px`;
    drag.el.style.top = `${y - NOTE_HEIGHT / 2}px`;
  };

  const onNotePointerUp = (note: StickyNote) => (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = noteDrag.current;
    if (!drag || drag.id !== note.id) return;
    noteDrag.current = null;
    if (drag.moved < TAP_THRESHOLD_PX) {
      // Restore original position (a click shouldn't nudge the note) and open the detail.
      drag.el.style.left = `${drag.origX - NOTE_WIDTH / 2}px`;
      drag.el.style.top = `${drag.origY - NOTE_HEIGHT / 2}px`;
      setDetailNote(note);
      return;
    }
    const scale = viewRef.current.scale;
    const x = Math.min(Math.max(drag.origX + (e.clientX - drag.startClientX) / scale, NOTE_WIDTH / 2), BOARD_WIDTH - NOTE_WIDTH / 2);
    const y = Math.min(Math.max(drag.origY + (e.clientY - drag.startClientY) / scale, NOTE_HEIGHT / 2), BOARD_HEIGHT - NOTE_HEIGHT / 2);
    moveNote.mutate({ id: note.id, x, y });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const mutation = deleteTarget.own ? deleteOwnNote : deleteAdminNote;
    mutation.mutate(deleteTarget.id);
    setDeleteTarget(null);
    setDetailNote(null);
  };

  const startPlacement = (imageData: string, paperColor: string, pinColor: string, poll: boolean) => {
    setShowEditor(false);
    setPendingNote({ imageData, paperColor, pinColor, poll });
    // Ghost starts in the middle of the current viewport.
    const vp = viewportRef.current;
    if (vp) {
      const rect = vp.getBoundingClientRect();
      const { x, y } = toBoardCoords(rect.left + vp.clientWidth / 2, rect.top + vp.clientHeight / 2);
      ghostPos.current = { x, y };
    }
  };

  const authorName = user?.username ?? "";
  const voteTitles = {
    yes: t("boardPollYes") as string,
    no: t("boardPollNo") as string,
    retract: t("boardPollRetract") as string,
  };
  // The detail modal holds a snapshot — re-resolve it from the cache so votes cast
  // inside the modal (or by others via refetch) show up immediately.
  const freshDetailNote = detailNote ? notes?.find((n) => n.id === detailNote.id) ?? detailNote : null;

  if (!validated) {
    return (
      <div className="base-container">
        <div className="content board-gate">{t("loading")}</div>
      </div>
    );
  }

  if (!canUseBoard) {
    return (
      <div className="base-container">
        <div className="header board-header">
          <span className="table-title">📌 {t("board")}</span>
        </div>
        <div className="content board-gate">
          <div className="board-gate-emoji">🔒</div>
          <p>{user ? t("boardLinkRequired") : t("boardLoginRequired")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="base-container board-page">
      <div className="header board-header">
        <span className="table-title">📌 {t("board")}</span>
        <div className="board-header-actions">
          <Button variant="primary" disabled={!!pendingNote || !!rotatingNote} onClick={() => setShowEditor(true)}>
            ✏️ {t("boardNewNote")}
          </Button>
        </div>
      </div>

      <div
        ref={viewportRef}
        className={`board-viewport${pendingNote ? " placing" : ""}${rotatingNote ? " rotating" : ""}`}
        onPointerDown={onViewportPointerDown}
        onPointerMove={onViewportPointerMove}
        onPointerUp={onViewportPointerUp}
        onPointerCancel={onViewportPointerUp}
      >
        <div
          ref={surfaceRef}
          className="board-surface"
          style={{ width: BOARD_WIDTH, height: BOARD_HEIGHT }}
        >
          {notes?.map((note) => (
            <div
              key={note.id}
              data-note-id={note.id}
              className={`board-note${note.own ? " own" : ""}${rotatingNote?.id === note.id ? " rotating" : ""}`}
              style={{
                left: note.x - NOTE_WIDTH / 2,
                top: note.y - NOTE_HEIGHT / 2,
                transform: `rotate(${note.rotation}deg)`,
              }}
              onPointerDown={note.own ? onNotePointerDown(note) : undefined}
              onPointerMove={note.own ? onNotePointerMove : undefined}
              onPointerUp={note.own ? onNotePointerUp(note) : undefined}
            >
              <StickyNoteView
                authorName={note.authorName}
                imageData={note.imageData}
                paperColor={note.paperColor}
                pinColor={note.pinColor}
                showDelete={(note.own || admin) && !rotatingNote}
                onDelete={() => setDeleteTarget(note)}
                showRotate={note.own && !pendingNote && !rotatingNote}
                onRotate={() => startRotating(note)}
                rotateTitle={t("boardRotate") as string}
                poll={note.poll}
                votes={note.votes}
                onVote={!pendingNote && !rotatingNote ? (vote) => voteNote.mutate({ id: note.id, vote }) : undefined}
                voteTitles={voteTitles}
              />
            </div>
          ))}

          {pendingNote && (
            <div
              ref={ghostRef}
              className="board-note board-note-ghost"
              style={{
                left: ghostPos.current.x - NOTE_WIDTH / 2,
                top: ghostPos.current.y - NOTE_HEIGHT / 2,
              }}
            >
              <StickyNoteView
                authorName={authorName}
                imageData={pendingNote.imageData}
                paperColor={pendingNote.paperColor}
                pinColor={pendingNote.pinColor}
                poll={pendingNote.poll}
              />
            </div>
          )}
        </div>

        {!isLoading && notes?.length === 0 && !pendingNote && (
          <div className="board-empty">🗒️ {t("boardEmpty")}</div>
        )}

        {/* stopPropagation: otherwise the viewport captures the pointer and steals the click */}
        <div className="board-zoom-controls" onPointerDown={(e) => e.stopPropagation()}>
          <button type="button" title={t("boardZoomIn") as string} onClick={() => zoomFromCenter(1.3)}>+</button>
          <button type="button" title={t("boardZoomOut") as string} onClick={() => zoomFromCenter(1 / 1.3)}>−</button>
          <button type="button" title={t("boardZoomFit") as string} onClick={fitView}>⛶</button>
        </div>

        {pendingNote && (
          <div className="board-place-bar" onPointerDown={(e) => e.stopPropagation()}>
            <span className="board-place-hint">👇 {t("boardPlaceHint")}</span>
            <div className="board-place-actions">
              <Button variant="outline-light" size="sm" onClick={() => setPendingNote(null)}>
                {t("cancel")}
              </Button>
            </div>
          </div>
        )}

        {rotatingNote && (
          <div className="board-place-bar" onPointerDown={(e) => e.stopPropagation()}>
            <span className="board-place-hint">🔄 {t("boardRotateHint")}</span>
            <div className="board-place-actions">
              <Button variant="success" size="sm" onClick={confirmRotation}>
                ✓ {t("confirm")}
              </Button>
              <Button variant="outline-light" size="sm" onClick={cancelRotation}>
                {t("cancel")}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="board-tip">{t("boardMoveOwnHint")}</div>

      <AnimatePresence>
        {showEditor && (
          <NoteEditorModal
            authorName={authorName}
            onSave={startPlacement}
            onClose={() => setShowEditor(false)}
          />
        )}

        {freshDetailNote && (
          <motion.div
            key="note-detail"
            className="note-detail-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setDetailNote(null)}
          >
            <motion.div
              className="note-detail"
              initial={{ opacity: 0, scale: 0.8, rotate: ((freshDetailNote.rotation + 180) % 360 + 360) % 360 - 180 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <StickyNoteView
                authorName={freshDetailNote.authorName}
                imageData={freshDetailNote.imageData}
                paperColor={freshDetailNote.paperColor}
                pinColor={freshDetailNote.pinColor}
                showDelete={freshDetailNote.own || admin}
                onDelete={() => setDeleteTarget(freshDetailNote)}
                poll={freshDetailNote.poll}
                votes={freshDetailNote.votes}
                onVote={(vote) => voteNote.mutate({ id: freshDetailNote.id, vote })}
                voteTitles={voteTitles}
                className="sticky-note--detail"
              />
              <div className="note-detail-meta">
                {freshDetailNote.playerId ? (
                  <Link to={`/players/${freshDetailNote.playerId}`}>{freshDetailNote.authorName}</Link>
                ) : (
                  <span>{freshDetailNote.authorName}</span>
                )}
                <span className="note-detail-date">
                  {new Date(freshDetailNote.createdAt).toLocaleDateString(i18n.language === "cs" ? "cs-CZ" : "en-GB")}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}

        {deleteTarget && (
          <ConfirmModal
            title={t("boardDeleteNoteTitle")}
            message={t("boardDeleteNoteMessage")}
            confirm={t("delete")}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default Board;
